import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import type { UnifiedGithubApi } from '@runway/devkit';
import {
  WorkflowInputsResponse,
  WorkflowsResponse,
  WorkflowRunsResponse,
  WorkflowDispatchRequest,
  WorkflowDispatchResponse,
  WorkflowRunInfo,
  WorkflowInfo,
  WorkflowJobsResponse,
} from '../types';

export interface GitHubWorkflowApi {
  listWorkflows(owner: string, repo: string): Promise<WorkflowsResponse>;
  getWorkflow(owner: string, repo: string, workflowId: string): Promise<WorkflowInfo>;
  getWorkflowInputs(owner: string, repo: string, workflowId: string): Promise<WorkflowInputsResponse>;
  dispatchWorkflow(request: WorkflowDispatchRequest): Promise<WorkflowDispatchResponse>;
  getWorkflowRuns(owner: string, repo: string, workflowId: string, perPage?: number): Promise<WorkflowRunsResponse>;
  getWorkflowRun(owner: string, repo: string, runId: number): Promise<WorkflowRunInfo>;
  getWorkflowRunJobs(owner: string, repo: string, runId: number): Promise<WorkflowJobsResponse>;
}

export class GitHubWorkflowApiClient implements GitHubWorkflowApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  // Not yet used for any read calls (all requests are proxied through
  // github-backend), but wired in so future auth-routing changes to
  // UnifiedGithubService are picked up automatically without touching
  // this client.
  private readonly unifiedGithub?: UnifiedGithubApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi; unifiedGithub?: UnifiedGithubApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.unifiedGithub = options.unifiedGithub;
  }

  /**
   * Exposes the unified GitHub service so future entity-aware read paths in
   * this client can route through it without changing the plugin factory.
   */
  getUnifiedGithubApi(): UnifiedGithubApi | undefined {
    return this.unifiedGithub;
  }

  private async getBaseUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl('github');
  }

  async listWorkflows(owner: string, repo: string): Promise<WorkflowsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows/${owner}/${repo}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch workflows: ${response.statusText}`);
    }

    return response.json();
  }

  async getWorkflow(owner: string, repo: string, workflowId: string): Promise<WorkflowInfo> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows/${owner}/${repo}/${workflowId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch workflow: ${response.statusText}`);
    }

    return response.json();
  }

  async getWorkflowInputs(owner: string, repo: string, workflowId: string): Promise<WorkflowInputsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows/${owner}/${repo}/${workflowId}/inputs`);

    if (!response.ok) {
      throw new Error(`Failed to fetch workflow inputs: ${response.statusText}`);
    }

    return response.json();
  }

  async dispatchWorkflow(request: WorkflowDispatchRequest): Promise<WorkflowDispatchResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows/dispatch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to dispatch workflow: ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  async getWorkflowRuns(owner: string, repo: string, workflowId: string, perPage = 10): Promise<WorkflowRunsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/workflows/${owner}/${repo}/${workflowId}/runs?per_page=${perPage}`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch workflow runs: ${response.statusText}`);
    }

    return response.json();
  }

  async getWorkflowRun(owner: string, repo: string, runId: number): Promise<WorkflowRunInfo> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows/${owner}/${repo}/runs/${runId}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch workflow run: ${response.statusText}`);
    }

    return response.json();
  }

  async getWorkflowRunJobs(owner: string, repo: string, runId: number): Promise<WorkflowJobsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/workflows/${owner}/${repo}/runs/${runId}/jobs`);

    if (!response.ok) {
      throw new Error(`Failed to fetch workflow run jobs: ${response.statusText}`);
    }

    return response.json();
  }
}
